import { z } from 'zod';
import {
  chatRagScopeDocumentSchema,
  RAG_SCOPE_MAX_DOCUMENTS,
  type ChatRagScopeDocumentDTO,
} from '@shared/schemas/chat';

export const CHAT_SESSION_INTENTS = ['general', 'document-search'] as const;

export type ChatSessionIntent = (typeof CHAT_SESSION_INTENTS)[number];

export const CHAT_GENERAL_PATH = '/chat';
export const CHAT_RAG_PATH = '/rag';
export const LEGACY_CHAT_RAG_PATH = '/chat/rag';

export type ChatLocationState = {
  chatIntent?: ChatSessionIntent;
  newChatKey?: string;
  /** Documents a new RAG chat starts limited to (e.g. « Discuter avec ce document »). */
  ragScope?: ChatRagScopeDocumentDTO[];
};

export type ParsedChatLocationState = {
  newChatKey?: string;
  ragScope?: ChatRagScopeDocumentDTO[];
};

const ragScopeStateSchema = z.array(chatRagScopeDocumentSchema).min(1).max(RAG_SCOPE_MAX_DOCUMENTS);

export function parseChatLocationState(state: unknown): ParsedChatLocationState {
  if (!state || typeof state !== 'object') {
    return {};
  }

  const record = state as Record<string, unknown>;
  const ragScope = ragScopeStateSchema.safeParse(record.ragScope);

  return {
    newChatKey: typeof record.newChatKey === 'string' ? record.newChatKey : undefined,
    ragScope: ragScope.success ? ragScope.data : undefined,
  };
}

/** Opens a new RAG chat limited to the given documents. */
export function buildRagScopedChatNavigation(documents: ChatRagScopeDocumentDTO[]) {
  return {
    path: CHAT_RAG_PATH,
    state: { ...buildNewChatLocationState(), ragScope: documents } satisfies ChatLocationState,
  };
}

export function buildNewChatPath(intent: ChatSessionIntent) {
  return intent === 'document-search' ? CHAT_RAG_PATH : CHAT_GENERAL_PATH;
}

export function buildConversationPath(conversationId: string, intent: ChatSessionIntent) {
  return `${buildNewChatPath(intent)}/${conversationId}`;
}

export function buildNewChatLocationState(): ChatLocationState {
  return {
    newChatKey: crypto.randomUUID(),
  };
}

export function isRagSpacePath(pathname: string) {
  return pathname === CHAT_RAG_PATH || pathname.startsWith(`${CHAT_RAG_PATH}/`);
}

export function isChatExperiencePath(pathname: string) {
  return pathname === CHAT_GENERAL_PATH
    || pathname.startsWith(`${CHAT_GENERAL_PATH}/`)
    || isRagSpacePath(pathname);
}

export function resolveChatIntentFromPathname(pathname: string): ChatSessionIntent {
  if (isRagSpacePath(pathname) || pathname === LEGACY_CHAT_RAG_PATH || pathname === `${LEGACY_CHAT_RAG_PATH}/`) {
    return 'document-search';
  }

  return 'general';
}

export function resolveChatSessionIntent(input: {
  conversationId?: string;
  conversationRagEnabled?: boolean;
  pathname: string;
}): ChatSessionIntent {
  if (input.conversationId && typeof input.conversationRagEnabled === 'boolean') {
    return input.conversationRagEnabled ? 'document-search' : 'general';
  }

  return resolveChatIntentFromPathname(input.pathname);
}

export function extractChatConversationId(pathname: string) {
  const ragMatch = /^\/rag\/([^/]+)$/.exec(pathname);

  if (ragMatch) {
    return ragMatch[1];
  }

  const chatMatch = /^\/chat\/([^/]+)$/.exec(pathname);

  if (!chatMatch || chatMatch[1] === 'rag') {
    return undefined;
  }

  return chatMatch[1];
}

export function isChatNewRoute(pathname: string) {
  return pathname === CHAT_GENERAL_PATH
    || pathname === `${CHAT_GENERAL_PATH}/`
    || pathname === CHAT_RAG_PATH
    || pathname === `${CHAT_RAG_PATH}/`;
}

export function resolveNewChatRagEnabled(intent: ChatSessionIntent) {
  return intent === 'document-search';
}

export function resolveNewChatWebSearchEnabled(
  intent: ChatSessionIntent,
  defaultEnabled = true,
) {
  return intent === 'general' ? defaultEnabled : false;
}

export function buildRouteComposerKey(input: {
  chatIntent: ChatSessionIntent;
  conversationId?: string;
  newChatKey?: string;
}) {
  if (input.conversationId) {
    return input.conversationId;
  }

  return `new:${input.newChatKey ?? 'default'}:${input.chatIntent}`;
}

/** The « new » buttons are current only on their empty route, never on an open conversation. */
export function resolveSidebarChatNavActiveState(pathname: string) {
  return {
    isDocumentSearchActive: pathname === CHAT_RAG_PATH || pathname === `${CHAT_RAG_PATH}/`,
    isGeneralChatActive: pathname === CHAT_GENERAL_PATH || pathname === `${CHAT_GENERAL_PATH}/`,
  };
}
